import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { formatEUR } from "@/lib/format";
import { FadeUp } from "@/components/Motion";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import PayPalCheckout from "@/components/PayPalCheckout";

export function Checkout() {
  const { items, subtotal, clear, discount, discountAmount, total } = useCart();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => { if (items.length === 0) nav("/shop"); }, [items, nav]);
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({})); }, []);

  const paySumUp = async (e) => {
    e.preventDefault();
    if (!email) { toast.error(t.checkout.email); return; }
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })),
        origin_url: window.location.origin,
        email,
      };
      if (discount?.type === "discount") payload.discount_code = discount.code_raw || discount.code;
      if (discount?.type === "gift_card") payload.gift_card_code = discount.code_raw || discount.code;
      const { data } = await api.post("/sumup/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })),
        origin_url: window.location.origin,
        email: email || undefined,
      };
      if (discount?.type === "discount") payload.discount_code = discount.code_raw || discount.code;
      if (discount?.type === "gift_card") payload.gift_card_code = discount.code_raw || discount.code;
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-32 min-h-screen" data-testid="checkout-page">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
        <FadeUp>
          <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-6">/ {t.checkout.eyebrow}</div>
          <h1 className="font-editorial italic font-light text-5xl text-cream mb-10">{t.checkout.title}</h1>
          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            <div>
              <label className="font-display text-[10px] tracking-[0.3em] text-cream/60 mb-3 block">{t.checkout.email}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="checkout-email"
                className="bg-transparent border-b border-white/20 focus:border-gold text-cream py-3 outline-none w-full"
              />
            </div>

            {/* Payment options */}
            {config?.sumup_enabled ? (
              <button
                type="button"
                onClick={paySumUp}
                disabled={busy || !email}
                data-testid="pay-sumup"
                className="w-full py-5 bg-gold text-black font-display text-[11px] tracking-[0.3em] hover:bg-gold-light gold-shine disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#C9A24C" }}
              >
                {busy ? t.checkout.processing : `→ ${t.checkout.pay_card} · ${formatEUR(total, lang)}`}
              </button>
            ) : (
              <div className="border border-gold/30 bg-gold/[0.03] p-6" data-testid="checkout-provider-notice">
                <div className="font-display text-[10px] tracking-[0.35em] text-gold mb-3">/ COMING SOON</div>
                <p className="font-editorial italic text-xl text-cream mb-2">{t.checkout.coming_soon_t}</p>
                <p className="text-cream/60 text-sm font-body leading-relaxed">
                  {t.checkout.coming_soon_s}
                </p>
                <a
                  href="https://wa.me/393793407070"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="checkout-whatsapp"
                  className="inline-flex items-center gap-2 mt-5 border border-gold/60 text-gold hover:bg-gold hover:text-black px-4 py-2.5 font-display text-[10px] tracking-[0.3em] transition-colors"
                >
                  <svg viewBox="0 0 32 32" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.33.245-.688.245-1.06 0-.058 0-.144-.014-.216-.101-.36-.144-.372-.816-.688-.616-.286-1.202-.5-1.876-.688zM16.147 4.5c-6.352 0-11.5 5.148-11.5 11.5 0 2.078.556 4.02 1.53 5.702L4.5 27.5l5.926-1.548A11.474 11.474 0 0 0 16.147 27.5c6.352 0 11.5-5.148 11.5-11.5s-5.148-11.5-11.5-11.5zm0 21.005c-1.75 0-3.4-.487-4.803-1.318l-.344-.202-3.567.938.958-3.481-.229-.36a9.482 9.482 0 0 1-1.462-5.082c0-5.248 4.267-9.515 9.515-9.515s9.515 4.267 9.515 9.515-4.269 9.505-9.583 9.505z"/>
                  </svg>
                  +39 379 340 7070
                </a>
              </div>
            )}

            <div className="flex items-center gap-4 py-2">
              <div className="flex-1 h-px bg-white/10" />
              <span className="font-display text-[10px] tracking-[0.3em] text-cream/50">{t.checkout.or}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <PayPalCheckout email={email} />

            <p className="text-[10px] text-cream/40 font-body text-center pt-2">
              {t.checkout.secure}
            </p>
          </form>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="border border-white/10 p-8 bg-[#0d0d0d]">
            <div className="font-display text-[10px] tracking-[0.3em] text-gold mb-6">/ {t.checkout.your_order}</div>
            <ul className="space-y-4 mb-6">
              {items.map((i) => (
                <li key={i.product.id} className="flex justify-between items-center text-sm">
                  <div>
                    <div className="text-cream font-display text-[11px] tracking-[0.2em]">{i.product[`name_${lang}`] || i.product.name_en}</div>
                    <div className="text-cream/40 text-xs">×{i.qty} · {i.product.weight_g} g ℮</div>
                  </div>
                  <span className="text-gold font-editorial">{formatEUR(i.qty * i.product.price_eur, lang)}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-white/10 pt-4 space-y-2">
              <div className="flex justify-between items-baseline text-cream/60 text-sm">
                <span className="font-display text-[10px] tracking-[0.3em]">{t.cart.subtotal}</span>
                <span className="font-editorial">{formatEUR(subtotal, lang)}</span>
              </div>
              {discount && (
                <div className="flex justify-between items-baseline text-gold text-sm" data-testid="checkout-discount-row">
                  <span className="font-display text-[10px] tracking-[0.3em]">{discount.code}</span>
                  <span className="font-editorial">−{formatEUR(discountAmount, lang)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-2 border-t border-white/10">
                <span className="font-display text-[10px] tracking-[0.3em] text-cream/80">TOTAL</span>
                <span className="font-editorial text-3xl text-gold" data-testid="checkout-total">{formatEUR(total, lang)}</span>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling");
  const { clear } = useCart();
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (attempts < 20) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch { setStatus("error"); }
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="pt-40 pb-32 min-h-screen flex items-center justify-center" data-testid="payment-success-page">
      <FadeUp className="max-w-lg text-center px-6">
        {status === "polling" && (
          <>
            <div className="w-16 h-16 border border-gold border-t-transparent rounded-full animate-spin mx-auto mb-8" />
            <p className="font-display text-[11px] tracking-[0.3em] text-cream/60">{t.checkout.verifying}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-gold mx-auto mb-8" />
            <h1 className="font-editorial italic font-light text-6xl text-cream mb-6">{t.checkout.success_t}</h1>
            <p className="text-cream/70 font-body text-lg mb-10">{t.checkout.success_s}</p>
            <Link to="/shop" data-testid="continue-shopping" className="inline-block px-8 py-4 bg-gold text-black font-display text-[11px] tracking-[0.3em] gold-shine" style={{ background: "#C9A24C" }}>
              → {t.cart.continue}
            </Link>
          </>
        )}
        {(status === "pending" || status === "error") && (
          <>
            <p className="font-editorial italic text-3xl text-cream mb-6">{t.checkout.pending}</p>
            <Link to="/shop" className="text-gold font-display text-[11px] tracking-[0.3em] link-underline">→ {t.checkout.continue}</Link>
          </>
        )}
      </FadeUp>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  return (
    <div className="pt-40 pb-32 min-h-screen flex items-center justify-center" data-testid="payment-cancel-page">
      <FadeUp className="max-w-lg text-center px-6">
        <XCircle className="w-16 h-16 text-cream/40 mx-auto mb-8" />
        <h1 className="font-editorial italic font-light text-6xl text-cream mb-6">{t.checkout.cancel_t}</h1>
        <p className="text-cream/70 font-body text-lg mb-10">{t.checkout.cancel_s}</p>
        <Link to="/checkout" data-testid="retry-checkout" className="inline-block px-8 py-4 bg-gold text-black font-display text-[11px] tracking-[0.3em] gold-shine" style={{ background: "#C9A24C" }}>
          → {t.cart.checkout}
        </Link>
      </FadeUp>
    </div>
  );
}
